export type Task = () => Promise<any>;

export interface TaskQueueOptions {
  /**
   * Number of conccurent tasks that can run at any given time.
   * Cannot be a negative number or zero (will throw error)
   */
  concurrency: number;
  /**
   * Number of retries that the taskqueue will execute for each task that
   */
  maxRetries: number;

  /**
   * Autostart
   */
  autoStart: boolean;
}

class QueuedTask {
  constructor(public task: Task, public tries = 0) {}
}

export class TaskQueue {
  private _queuedTasks: QueuedTask[] = [];

  private _runningTasks: QueuedTask[] = [];

  private options: TaskQueueOptions = {
    concurrency: 1,
    maxRetries: 0,
    autoStart: true,
  };

  private _running = true;

  public start() {
    this._running = true;
    this._updateQueue();
  }

  public stop() {
    // Stops running on the next updateQueue ycle
    this._running = false;
  }

  constructor(options: Partial<TaskQueueOptions> = {}) {
    Object.assign(this.options, options);

    const { concurrency, maxRetries, autoStart } = this.options;
    if (concurrency <= 0) {
      throw new Error(
        `Invalid concurrency of ${concurrency}. Needs to be atleast 1.`
      );
    }

    if (maxRetries < 0) {
      throw new Error(
        `Invalid maxRetries of ${maxRetries}. Needs to be atleast 0.`
      );
    }

    this._running = autoStart;
  }

  /**
   * Enqueue tasks and run them immediately when the queue is running
   * (e.g not stopped)
   * @param tasks The tasks that are added to the queue
   */
  public enqueue(tasks: Task[]) {
    this._queuedTasks.push(...tasks.map((task) => new QueuedTask(task)));
    this._updateQueue();
  }

  private async _updateQueue() {
    if (!this._running) return;
    while (this._runningTasks.length < this.options.concurrency) {
      const task = this._queuedTasks.shift();
      if (!task) return;

      this._runningTasks.push(task);
      this._runSingleTask(task);
    }
  }

  private async _runSingleTask(queuedTask: QueuedTask) {
    try {
      await queuedTask.task();
      this._removeRunningTask(queuedTask);
    } catch {
      this._removeRunningTask(queuedTask);

      queuedTask.tries++;

      if (queuedTask.tries <= this.options.maxRetries) {
        // When a task fails and can be retried, we add it to the front
        // of the queued task, so that next time when _updateQueue is called,
        // it will be first in line again
        this._queuedTasks.unshift(queuedTask);
      }
    } finally {
      this._updateQueue();
    }
  }

  private async _removeRunningTask(queuedTask: QueuedTask) {
    // Remove the task that was just completed
    this._runningTasks = this._runningTasks.filter((t) => t !== queuedTask);
  }
}
